import { BookOpen, PlugZap, Search } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const catalogIcon = (
  <BookOpen className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)
const providersIcon = (
  <PlugZap className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

const ACTION_FIELD_EXCLUDE = new Set([
  'id',
  'toolId',
  'name',
  'description',
  'controlMappings',
  'variables',
])

function scalarFields(record: Record<string, unknown>) {
  return Object.entries(record)
    .filter(
      ([key, value]) => !ACTION_FIELD_EXCLUDE.has(key) && formatToolValue(value)
    )
    .slice(0, 24)
    .map(([key, value]) => ({
      label: key,
      value: formatToolValue(value),
      tone: normalizeTone(value),
    }))
}

function queryDetail(args: Record<string, unknown>) {
  const parts = [
    pickString(args.query),
    Array.isArray(args.providers) && args.providers.length > 0
      ? `providers: ${args.providers.join(', ')}`
      : null,
    Array.isArray(args.controlRefs) && args.controlRefs.length > 0
      ? `controls: ${args.controlRefs.join(', ')}`
      : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

function actionTitle(action: Record<string, unknown>) {
  return (
    pickString(
      action.toolId,
      action.id,
      action.name,
      action.action
    ) ?? 'Procedure action'
  )
}

function actionMeta(action: Record<string, unknown>) {
  return [
    Array.isArray(action.providers)
      ? action.providers.join(', ')
      : formatToolValue(action.provider),
    formatToolValue(action.tier),
    action.connected === true
      ? 'connected'
      : action.connected === false
        ? 'not connected'
        : null,
  ]
}

function actionSections(action: Record<string, unknown>) {
  return [
    {
      title: 'Controls',
      content: Array.isArray(action.controlMappings)
        ? action.controlMappings.map((control) => `- ${control}`).join('\n')
        : null,
    },
    {
      title: 'Variables',
      content: Array.isArray(action.variables)
        ? action.variables
            .map((variable) => {
              if (!variable || typeof variable !== 'object') return null
              const record = variable as Record<string, unknown>
              const required = record.required ? 'required' : 'optional'
              return `- **${pickString(record.id, record.label) ?? 'Variable'}** (${formatToolValue(record.type) ?? 'value'}, ${required})`
            })
            .filter(Boolean)
            .join('\n')
        : null,
    },
  ]
}

function actionsFrom(result: unknown): Array<Record<string, unknown>> {
  if (!result || typeof result !== 'object') return []
  const actions = (result as Record<string, unknown>).actions
  return Array.isArray(actions)
    ? actions.filter(
        (action): action is Record<string, unknown> =>
          !!action && typeof action === 'object'
      )
    : []
}

function providersFrom(result: unknown): Array<Record<string, unknown>> {
  if (!result || typeof result !== 'object') return []
  const providers = (result as Record<string, unknown>).providers
  return Array.isArray(providers)
    ? providers.filter(
        (provider): provider is Record<string, unknown> =>
          !!provider && typeof provider === 'object'
      )
    : []
}

function CatalogActionsMessage({
  part,
  loadingLabel,
  allowDetails,
}: ToolPartProps & { loadingLabel: string }) {
  const state = readToolState(part)
  const actions = actionsFrom(state.result)
  const total =
    typeof state.result?.total === 'number' ? state.result.total : actions.length
  const returned =
    typeof state.result?.returned === 'number'
      ? state.result.returned
      : actions.length

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel={loadingLabel}
        completedLabel={`${plural(returned, 'procedure action')} loaded`}
        loadingDetail={queryDetail(state.args)}
        completedDetail={
          total > returned ? `${returned}/${total}` : queryDetail(state.args)
        }
        completedIcon={catalogIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No procedure actions found'
          items={actions.map((action, index) => {
            const title = actionTitle(action)
            return {
              id: null,
              title,
              subtitle: pickString(action.name, action.action),
              meta: actionMeta(action),
              tone: normalizeTone(action.connected ? 'success' : 'default'),
              detailOverlay: allowDetails
                ? {
                    id: getToolCallId(part, `runbook-catalog-action-${index}`),
                    payload: {
                      entityType: 'runbook',
                      toolName: 'runbookCatalog',
                      title,
                      description: pickString(action.description),
                      fields: scalarFields(action),
                      sections: actionSections(action),
                    },
                  }
                : null,
            }
          })}
          moreLabel={total > returned ? `${total - returned} more` : null}
        />
      ) : null}
    </>
  )
}

export function RunbookCatalogListActionsToolMessage(props: ToolPartProps) {
  return (
    <CatalogActionsMessage
      {...props}
      allowDetails={false}
      loadingLabel='Listing procedure actions...'
    />
  )
}

export function RunbookCatalogSearchActionsToolMessage(props: ToolPartProps) {
  return (
    <CatalogActionsMessage
      {...props}
      allowDetails
      loadingLabel='Searching procedure actions...'
    />
  )
}

export function RunbookCatalogDescribeActionToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const title =
    pickString(state.result?.toolId, state.args?.toolId, state.result?.name) ??
    'Procedure action'

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Inspecting procedure action...'
        completedLabel='Procedure action loaded'
        loadingDetail={pickString(state.args?.toolId)}
        completedDetail={title}
        completedIcon={
          <Search className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
        }
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(part, `runbook-action-${title}`)}
          payload={{
            entityType: 'runbook',
            toolName: 'runbookCatalogDescribeAction',
            title,
            description: pickString(state.result.description),
            fields: scalarFields(state.result),
            sections: actionSections(state.result),
          }}
        />
      ) : null}
    </>
  )
}

export function RunbookCatalogListConnectedProvidersToolMessage({
  part,
}: ToolPartProps) {
  const state = readToolState(part)
  const providers = providersFrom(state.result)
  const connectedCount = providers.filter(
    (provider) => provider.connected === true
  ).length

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Checking connected providers...'
        completedLabel={`${plural(connectedCount, 'connected provider')} found`}
        completedIcon={providersIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No connected providers found'
          items={providers.map((provider) => {
            const title =
              pickString(provider.providerLabel, provider.name, provider.provider) ??
              'Provider'
            return {
              id: null,
              title,
              subtitle: pickString(provider.provider),
              meta: [
                provider.connected === true ? 'connected' : 'not connected',
                formatToolValue(provider.actionCount),
              ],
              tone: normalizeTone(provider.connected ? 'success' : 'default'),
            }
          })}
        />
      ) : null}
    </>
  )
}
